import { VectorLayer2 } from './Layer.js';
import { outlineCounty, outlineCountyLow } from './MapVectorStyles.js';
import * as LQ from '../controller/LayerQueries';
import { useState, useEffect, useContext, useRef } from 'react';
import { useAfterInit } from '../controller/useAfterInit.js';
import { InterfaceContext } from '../model/InterfaceProvider';
import MapContext from '../controller/MapProvider';
import * as mapStyles from '../view/MapVectorStyles.js';

const CountyLayer = (props) => {
    const getDetailLevel = (zoom) => (zoom < 8 ? 'low' : 'high');
    const generateDataSlug = () => [scope, tract, theme.slug, usState?.slug || 'us'].join('_');

    const { opacity, zIndex, onLoadStatus, map } = props;
    const { scope, tract, theme, year, usState, setErrorMessage, opacity: globalOpacity } = useContext(InterfaceContext);
    const { zoomRead } = useContext(MapContext);
    const { fips } = useContext(MapContext);

    const [detail, setDetail] = useState(() => getDetailLevel(zoomRead));
    const [localOpacity, setLocalOpacity] = useState(opacity);
    const [features, setFeatures] = useState([]);
    const [loadStatus, setLoadStatus] = useState(1);
    const [lyr, setLyr] = useState(null);
    const changeQueue = useRef(0);

    const [selected_data_slug, setSelectedDataSlug] = useState(() => generateDataSlug());

    const setBlendMode = (e) => (e.context.globalCompositeOperation = 'multiply');
    const updateSlug = () => setSelectedDataSlug(generateDataSlug());

    useEffect(() => setDetail(getDetailLevel(zoomRead)), [zoomRead]);
    useEffect(() => updateSlug(), [scope, theme, tract]);
    useEffect(() => setLocalOpacity((opacity || 1) * (globalOpacity / 100)), [opacity, globalOpacity]);
    useEffect(() => onLoadStatus(loadStatus), [loadStatus]);
    // useEffect(() => lyr && lyr.once('postrender', () => setLoadStatus(1)), [features]);
    useEffect(() => {
        if (lyr) {
            setLoadStatus(0);
            lyr.once('postrender', () => {
                changeQueue.current = changeQueue.current - 1;
                if (changeQueue.current < 1) {
                    changeQueue.current = 0;
                    setLoadStatus(1);
                }
            });
        }
    }, [features]);

    useAfterInit(() => {
        // console.log('$$$COUNTY selected_data_slug', selected_data_slug);
        setLoadStatus(0);
        refreshColors();
    }, [selected_data_slug]);

    useEffect(() => {
        // console.log('$$$county state updated', changeQueue.current);
        if (!features?.length) return;
        changeQueue.current = changeQueue.current + 1;
        setLoadStatus(0);
        refreshSVI();
    }, [usState]);

    useEffect(() => {
        // console.log('$$$county year, detail', changeQueue.current);
        if (!features?.length) return; //don't refresh on var init
        changeQueue.current = changeQueue.current + 1;
        setLoadStatus(0);
        refreshFeatures();
    }, [year, detail]);

    const getGeometry = async () => {
        var zoom = map.getView().getZoom();
        var newfeatures = await LQ.getVisibleGeometry({ zoom, scope: 'state', tract: 'county', state: usState, year: year.label }).catch((err) => {
            setErrorMessage(err.modalMessage, err);
            return [];
        });
        return newfeatures || [];
    };
    const getSvi = async () => {
        let localScope = usState ? 'state' : scope;

        return await LQ.getVisibleSVI({ theme: theme.id, year: year.label, scope: localScope, tract: 'county', state: usState, fips: [] }).catch((err) => {
            console.error('Failed to load SVI data in County Layer');
            console.error(err);
            setErrorMessage(err.modalMessage, err);
            setLoadStatus(1);
        });
    };
    const isEmpty = (obj) => {
        for (let prop in obj) return false;
        return true;
    };
    const decorateFeatures = (features, svi) => {
        var zoom = map.getView().getZoom();
        let defaultStyle = getDefaultStyle(svi);
        let localScope = usState ? 'state' : scope;
        return LQ.stylizeGeometry(features, localScope, theme, usState, svi, true, defaultStyle, zoom);
    };
    const refreshFeatures = async (success = () => {}, failure = () => {}) => {
        const newfeatures = await getGeometry(fips);
        const svi = await getSvi(fips);
        let decorated = decorateFeatures(newfeatures, svi);
        if (tract === 'census' && usState) {
            //ΔVΔVΔ: I don't know why clearing the styles fixes the county/overlay issue
            decorated = decorated.map((f) => {
                f.setStyle(outlineCounty);
                return f;
            });
        }
        setFeatures(decorated);
        success(decorated);
    };

    //switching from state to national causes double render
    const refreshSVI = async () => {
        const svi = await getSvi(fips);
        let decorated = decorateFeatures(features, svi);
        //if cenus and state set outline
        if (tract === 'census' && usState) {
            //ΔVΔVΔ: I don't know why clearing the styles fixes the county/overlay issue
            decorated = decorated.map((f) => {
                f.setStyle(outlineCounty);
                return f;
            });
        }
        setFeatures(decorated.slice(0)); // using slice tells react to treat the features as a new reference and trigger render listen
    };
    const getDefaultStyle = (svi = {}) => {
        var zoom = map.getView().getZoom();
        let zoomStyle = zoom > 6 ? outlineCounty : outlineCountyLow;
        return isEmpty(svi) && zoomStyle;
    };
    const featureWithinScope = (shape) => {
        if (scope == 'national') return true;
        if (!usState) return false;
        const fips = shape.get('FIPS') || shape.get('STATEFP');
        return fips.substr(0, 2) === usState.id + '';
    };
    const getStyleFromFeature = (shape) => {
        if (!featureWithinScope(shape)) return mapStyles.outOfScope;
        const val = shape.get('sviValues')['theme' + theme.id];
        return theme.colorScheme[val] || mapStyles.noData;
    };
    const restyleFeatures = (features) => {
        features.forEach((shape) => {
            let sviStyle = getStyleFromFeature(shape);
            if (typeof sviStyle == 'object') shape.setStyle(sviStyle);
        });
    };
    const outlineFeatures = (features) => {
        var zoom = map.getView().getZoom();
        let boundsStyle = zoom > 6 ? outlineCounty : outlineCountyLow;
        features.forEach((shape) => {
            shape.setStyle(boundsStyle);
        });
        return features;
    };
    const refreshColors = () => {
        if (tract === 'census') {
            outlineFeatures(features);
        } else {
            restyleFeatures(features);
        }
        setFeatures(features.slice(0));
    };
    return (
        <VectorLayer2
            title={'NationalCounty'}
            className={'ol-layer svi'}
            style={outlineCounty}
            opacity={localOpacity}
            features={features}
            loader={(extent, resolution, projection, success, failure) => {
                setLoadStatus(0);
                refreshFeatures(success, failure);
            }}
            zIndex={zIndex}
            map={map}
            onPreRender={setBlendMode}
            onAdded={(lyr) => setLyr(lyr)}
        />
    );
};
export default CountyLayer;
